import { CommonModule } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import Currency from 'currency.js';
import { NgxMaskModule } from 'ngx-mask';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import { AstraConnectionComponent } from '../../../../modules/astra/components/astra-connection/astra-connection.component';
import { AstraUserCardDTO } from '../../../../modules/astra/models/astra-user-cards.dto';
import { AstraService } from '../../../../modules/astra/services/astra.service';
import { AstraUserStatusEnum } from '../../../../modules/authentication/models/astra-user-status.enum';
import {
  BankAccountEntryType,
  V1IBankAccount,
} from '../../../../modules/authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../../modules/authentication/services/authentication.service';
import { DashboardWrapperComponent } from '../../../../modules/dashboard-wrapper/dashboard-wrapper.component';
import { IIngoCard } from '../../../../modules/ingo/models/ingo-card.interface';
import { IngoService } from '../../../../modules/ingo/services/ingo.service';
import { CurrencyJsPipe } from '../../../../modules/pipes/currency-js.pipe';
import { LocalisePipe } from '../../../../modules/pipes/localise.pipe';
import { DisableAutofillDirective } from '../../../../modules/shared/directives/disable-autofill.directive';
import { BankService } from '../../../../modules/shared/services/bank.service';
import { BrokerService } from '../../../../modules/shared/services/broker.service';
import { LocaliseService } from '../../../../modules/shared/services/localise.service';
import { PlaidService } from '../../../../modules/shared/services/plaid.service';
import { SharedService } from '../../../../modules/shared/services/shared.service';
import { ThemesService } from '../../../../services/themes.service';
import { AddNewBankAccountComponent } from '../../components/add-new-bank-account/add-new-bank-account.component';
import { IPartner } from '../../models/partner';
import { IPayrollSettingsList } from '../../models/payroll-settings-list.interface';
import { PayrollSettingsTypeEnum } from '../../models/payroll-settings-type.enum';
import { PayrollSettingsViewTypeEnum } from '../../models/payroll-settings-view-type.enum';
import { PayrollSettingsService } from '../../services/payroll-settings.service';

@Component({
  selector: 'app-dashboard-payroll-settings',
  templateUrl: './dashboard-payroll-settings.component.html',
  styleUrls: ['./dashboard-payroll-settings.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    LocalisePipe,
    CurrencyJsPipe,
    FormsModule,
    NgxMaskModule,
    DisableAutofillDirective,
    DashboardWrapperComponent,
  ],
})
export class DashboardPayrollSettingsComponent
  extends AstraConnectionComponent
  implements OnInit
{
  @ViewChild('editModalTmp') editModalTmp: TemplateRef<any>;

  public edit = false;
  public loading = true;
  public equalPercent = true;
  public defaultPayrollSettings = false;
  public totalEqual: Currency = new Currency(0);
  public list: IPayrollSettingsList[] = [];
  public bankAccounts: V1IBankAccount[] = [];
  public bankAccountsMap?: Record<number, V1IBankAccount>;
  public ingoCards: IIngoCard[] = [];
  public ingoCardsMap: Record<number, IIngoCard> = {};
  public percentIngoCardsMap: Record<number | string, Currency> = {};
  public editPercentIngoCardsMap: Record<number | string, number> = {};
  public percentBankAccountsMap: Record<number | string, Currency>;
  public usingExternalBankAccount = false;
  public editPercentBankAccountsMap: Record<number | string, number> = {};
  public percentBrokerBankMap: Record<number | string, Currency>;
  public editPercentBrokerBankMap: Record<number | string, number> = {};
  public instantFeePercent = Currency(this.defaultInstantFeePercent);
  public astraCards: AstraUserCardDTO[] = [];
  public astraCardsMap: Record<string, AstraUserCardDTO> = {};
  public dialogRef: MatDialogRef<any>;
  public showTooltipStandard = false;
  public showTooltipInstant = false;
  public changeData = false;
  public changePercent = false;
  public wasActive = false;
  public externalFeePercent = 0.5;
  public partnerList?: IPartner[];
  public isExternalAccount = false;

  constructor(
    public toastrService: ToastrService,
    public sharedService: SharedService,
    public authenticationService: AuthenticationService,
    public dialog: MatDialog,
    private router: Router,
    public astraService: AstraService,
    private bankService: BankService,
    private plaidService: PlaidService,
    private ingoService: IngoService,
    private localiseService: LocaliseService,
    private payrollSettingsService: PayrollSettingsService,
    public themesService: ThemesService,
    private activatedRoute: ActivatedRoute,
    public brokerService: BrokerService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    await this.loadAll();

    const view: PayrollSettingsViewTypeEnum = Number(
      this.activatedRoute.snapshot.queryParams.view,
    );
    if (view === PayrollSettingsViewTypeEnum.EditDistributionsModal) {
      this.editDistributions(this.editModalTmp);
    }
  }

  private async loadAll(): Promise<void> {
    await Promise.all([
      await this.load(),
      await this.loadBankAccounts(),
      await this.getPayrollSettings(),
      await this.loadIngoCards(),
      firstValueFrom(this.astraService.getWithdrawalInfo()).then(
        (info) => (this.instantFeePercent = Currency(info.instantFeePercent)),
      ),
    ]);

    const user = this.authenticationService.getProfileState();
    if (
      user.astraIsConnected &&
      user.astraUserStatus === AstraUserStatusEnum.Approved
    ) {
      await this.loadAstraCards({});
      if (this.astraCards) {
        this.astraCards.forEach((card) => {
          for (const item of this.list) {
            if (item.externalCardId === card.id) {
              this.percentBankAccountsMap[card.id] = item.percent;
              break;
            }
          }
        });
      }
    }

    this.partnerList = await this.brokerService.getPartnersList().toPromise();
    this.partnerList?.forEach((item) => {
      if (item.brokerBankAccount.length) {
        this.isExternalAccount = true;
      }
    });
    this.loading = false;
  }

  public async loadIngoCards(): Promise<void> {
    try {
      this.ingoCards = (await this.ingoService.getCards().toPromise()) || [];
      this.ingoCardsMap = {};
      this.ingoCards.forEach((card) => {
        this.ingoCardsMap[card.id] = card;
      });
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }
  }

  public async applySettingsAll(): Promise<void> {
    this.loading = true;
    this.defaultPayrollSettings = !this.defaultPayrollSettings;
    try {
      await this.payrollSettingsService
        .payrollSettingsDefaultActive(this.defaultPayrollSettings)
        .toPromise();
      await this.getPayrollSettings();
      this.toastrService.success('Success!');
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }
    this.loading = false;
  }

  private async load(): Promise<void> {
    this.loading = true;

    try {
      this.list = await firstValueFrom(this.payrollSettingsService.get());
      this.percentBankAccountsMap = {};
      this.percentBrokerBankMap = {};
      this.percentIngoCardsMap = {};
      this.usingExternalBankAccount = false;
      for (const payroll of this.list) {
        if (payroll.bankAccountId) {
          this.percentBankAccountsMap[payroll.bankAccountId] = payroll.percent;
        }
        if (payroll.ingoCardId) {
          this.percentIngoCardsMap[payroll.ingoCardId] = payroll.percent;
        }
        if (payroll.brokerBankAccountId) {
          this.percentBrokerBankMap[payroll.brokerBankAccountId] =
            payroll.percent;
          this.usingExternalBankAccount = true;
        }
      }
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }
  }

  public async save(): Promise<void> {
    const activePayrollDefault = this.defaultPayrollSettings;
    this.calcPercent();
    if (!this.equalPercent) {
      return;
    }
    this.defaultPayrollSettings = activePayrollDefault;
    this.loading = true;

    const updateList: IPayrollSettingsList[] = [];

    this.bankAccounts.forEach((item) => {
      if (this.editPercentBankAccountsMap[item.id] > 0) {
        const newItem: IPayrollSettingsList = {
          percent: Currency(this.editPercentBankAccountsMap[item.id]),
          type: PayrollSettingsTypeEnum.BankAccount,
          bankAccountId: item.id,
          bankAccount: item,
          active: true,
        };
        updateList.push(newItem);
      }
    });

    this.astraCards.forEach((item) => {
      if (this.editPercentBankAccountsMap[item.id] > 0) {
        const newItem: IPayrollSettingsList = {
          externalCardId: item.id,
          percent: Currency(this.editPercentBankAccountsMap[item.id]),
          type: PayrollSettingsTypeEnum.AstraDebitCard,
          active: true,
        };
        updateList.push(newItem);
      }
    });

    this.ingoCards.forEach((item) => {
      if (this.editPercentIngoCardsMap[item.id] > 0) {
        const newItem: IPayrollSettingsList = {
          ingoCardId: item.id,
          percent: Currency(this.editPercentIngoCardsMap[item.id]),
          type: PayrollSettingsTypeEnum.IngoCard,
          active: true,
        };
        updateList.push(newItem);
      }
    });

    this.partnerList?.forEach((item) => {
      item.brokerBankAccount.forEach((bank) => {
        if (this.editPercentBrokerBankMap[bank.id] > 0) {
          const newItem: IPayrollSettingsList = {
            brokerBankAccountId: bank.id,
            percent: Currency(this.editPercentBrokerBankMap[bank.id]),
            type: PayrollSettingsTypeEnum.ExternalBrokerAccount,
            active: true,
          };
          updateList.push(newItem);
        }
      });
    });

    try {
      await Promise.all([
        this.payrollSettingsService
          .update({
            list: updateList,
          })
          .toPromise(),
        this.payrollSettingsService
          .payrollSettingsDefaultActive(this.defaultPayrollSettings)
          .toPromise(),
      ]);
      this.loadAll();
      this.dialogRef?.close();
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }

    this.loading = false;
  }

  private openDialog(template: TemplateRef<any>): void {
    this.dialogRef = this.dialog.open(template, {
      width: '80vw',
      panelClass: [
        'dialog-without-padding',
        this.themesService.getDarkThemeClass(),
      ],
      backdropClass: this.themesService.getDarkThemeClass(),
    });
  }

  public openBankAccount(): void {
    this.router.navigate(['/bank-accounts']);
  }

  public editDistributions(template: TemplateRef<any>): void {
    if (this.loading) {
      return;
    }

    for (const key in this.percentBankAccountsMap) {
      this.editPercentBankAccountsMap[key] =
        this.percentBankAccountsMap[key].value;
    }
    for (const key in this.percentIngoCardsMap) {
      this.editPercentIngoCardsMap[key] = this.percentIngoCardsMap[key].value;
    }

    for (const key in this.percentBrokerBankMap) {
      this.editPercentBrokerBankMap[key] = this.percentBrokerBankMap[key].value;
    }

    if (this.defaultPayrollSettings) {
      this.wasActive = true;
    }

    this.calcPercent();
    this.openDialog(template);
  }

  public calcPercent(changeData?: boolean): void {
    this.changePercent = true;
    if (changeData && !this.changeData) {
      this.changeData = true;
    }
    this.totalEqual = Currency(0);
    for (const key in this.editPercentBankAccountsMap) {
      this.totalEqual = this.totalEqual.add(
        Currency(this.editPercentBankAccountsMap[key]),
      );
    }
    for (const key in this.editPercentIngoCardsMap) {
      this.totalEqual = this.totalEqual.add(
        Currency(this.editPercentIngoCardsMap[key]),
      );
    }

    for (const key in this.editPercentBrokerBankMap) {
      this.totalEqual = this.totalEqual.add(
        Currency(this.editPercentBrokerBankMap[key]),
      );
    }

    this.equalPercent = this.totalEqual.value === 100;
    if ((this.list?.length === 0 && this.equalPercent) || this.wasActive) {
      this.defaultPayrollSettings = true;
    }

    if (!this.equalPercent) {
      this.defaultPayrollSettings = false;
    }

    this.changePercent = false;
  }

  private async loadBankAccounts(): Promise<void> {
    const user = await this.authenticationService.getProfileState();

    // if (!!user?.plaidConnections?.length) {
    //   try {
    //     await Promise.all(
    //       user.plaidConnections.map((c) =>
    //         this.plaidService.sync(c.id).toPromise(),
    //       ),
    //     );
    //   } catch (e) {}
    // }

    this.bankAccounts = await firstValueFrom(this.bankService.getList());
    this.bankAccounts.forEach((bankAccount) => {
      bankAccount.accountNumber = bankAccount.accountNumber.slice(-10);
    });
    this.bankAccountsMap = {};
    for (const account of this.bankAccounts) {
      this.bankAccountsMap[account.id] = account;
    }
  }

  public async getPayrollSettings(): Promise<void> {
    const responseStatus = await firstValueFrom(
      this.payrollSettingsService.getPayrollSettingsDefaultActive(),
    );
    this.defaultPayrollSettings = responseStatus.success;
  }

  public showTooltip(): void {
    this.showTooltipStandard = !this.showTooltipStandard;
  }
  public showHideTooltipInstant(): void {
    this.showTooltipInstant = !this.showTooltipInstant;
  }
}
